/* @flow */

import * as React from 'react';
import glamorous from 'glamorous';
import colorUtils from 'color';
import { tint } from 'polished';
import numeral from 'numeral';
import { get } from 'lodash/fp';

import { hover } from 'app/utils/cssUtils';
import AnimateNumber from 'app/components/commons/AnimateNumber';
import Columns from 'app/components/commons/Columns';
import Responsive from 'app/components/responsive/Responsive';
import PlayerAvatar from 'app/components/challenge/PlayerAvatar';
import PlayerBar from 'app/components/challenge/PlayerBar';
import PlayerDatum from 'app/components/challenge/PlayerDatum';
import RowButton from 'app/components/commons/RowButton';
import Tappable from 'app/components/commons/Tappable';
import { isElementInViewport } from 'app/utils/domUtils';
import LineGraph from './LineGraph';
import StarSymbol from './StarSymbol';

type Props = {
  awayTeam?: string,
  barrier?: number,
  canAfford: boolean,
  disabled?: boolean,
  gameOrder?: number,
  homeTeam?: string,
  id: string,
  idStr: string,
  image?: string,
  initials: string,
  isHome: boolean,
  isRacing?: boolean,
  isScratched?: boolean,
  isTeam: boolean,
  // isRacing: boolean,
  labels: Array<Object>,
  name: string,
  number?: number,
  onClick?: (id: string) => void,
  onRemove: (id: string, position: string) => void,
  onSelect: (id: string, positions: Array<Object>) => void,
  playerSelectionTableInfo: Object,
  positionStr: string,
  positions: Array<Object>,
  reverseOrder?: boolean,
  rider?: string,
  riderImage?: string,
  positionFilter: string | null,
  selected?: boolean,
  selectedPosition?: string,
  showWidget?: boolean,
  tableData?: Object,
  trainer?: string
};

const EntityWrapper = glamorous.div(
  ({ theme }) => ({
    alignItems: 'stretch',
    backgroundColor: theme.color.white,
    borderColor: theme.color.basis6,
    borderStyle: 'solid',
    borderWidth: 0,
    borderBottomWidth: 1,
    color: theme.color.basis1,
    display: 'flex',
    fontSize: '0.9rem',
    minWidth: 0,
    transition: 'background-color 0.2s'
  }),
  ({ highlight, theme }) =>
    highlight
      ? {
          backgroundColor: tint(0.5, theme.color.basis6),
          borderColor: 'rgba(0, 0, 0, 0)'
        }
      : {},
  ({ disabled, theme }) =>
    !disabled
      ? hover({
          backgroundColor: colorUtils(theme.color.white)
            .darken(0.05)
            .hex()
        })
      : {}
);

const EntityMain = glamorous.div({
  alignItems: 'center',
  display: 'flex',
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  minWidth: 0 // flexbox hack
});

const AvatarCell = glamorous.div({
  alignItems: 'center',
  display: 'flex',
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  paddingLeft: 10,
  paddingRight: 5
});

const ButtonCell = glamorous.div({
  alignItems: 'stretch',
  display: 'flex',
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  justifyContent: 'flex-end',
  width: '3rem'
});

const OpenEntityStats = glamorous.div(({ alignItems, theme }) => ({
  alignItems: alignItems || 'flex-end',
  display: 'flex',
  flexBasis: 'auto',
  flexDirection: 'column',
  flexGrow: 0,
  flexShrink: 0,
  justifyContent: 'space-between',
  textAlign: 'center',
  padding: '0.3em 0',
  [theme.mediaQuery.tabletPortraitAndUp]: {
    flexGrow: 0,
    alignItems: 'center',
    flexBasis: '16rem', // auto doesn't seem to work
    flexDirection: 'row-reverse',
    fontSize: '0.833rem',
    justifyContent: 'flex-end'
  }
}));

const OpenEntityStatsInfo = glamorous.div(({ theme }) => ({
  display: 'flex',
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  justifyContent: 'flex-end',
  [theme.mediaQuery.tabletPortraitAndUp]: {
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    justifyContent: 'flex-start'
  }
}));

const PlayerBarOuter = glamorous.div({
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  minWidth: 0, // flexbox hack
  position: 'relative'
});

const StyledAnimateNumber = glamorous(AnimateNumber)(({ disabled, theme, warn }) => ({
  color: warn ? theme.color.danger : theme.color.basis1,
  fontSize: '1rem',
  fontWeight: 600,
  opacity: disabled ? 0.25 : undefined,
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    fontSize: 'inherit'
  }
}));

const Column = glamorous.div({
  padding: '0 !important'
});

class OpenEntityRow extends React.PureComponent<Props> {
  props: Props;

  ref: HTMLElement; // eslint-disable-line react/sort-comp

  state = {
    animateSalary: true
  };

  static defaultProps = {
    awayTeam: '',
    barrier: undefined,
    disabled: false,
    gameOrder: undefined,
    homeTeam: '',
    image: undefined,
    isRacing: false,
    isScratched: false,
    number: undefined,
    onClick: undefined,
    reverseOrder: false,
    rider: undefined,
    riderImage: undefined,
    selected: false,
    selectedPosition: undefined,
    showWidget: false,
    tableData: undefined,
    trainer: undefined
  };

  componentDidUpdate(prevProps: Props) {
    const { showWidget } = this.props;

    if (showWidget && !prevProps.showWidget) {
      this.scrollTop();
    }
  }

  componentWillUpdate(nextProps: Props) {
    const { positionFilter } = this.props;

    // we don't want to animate the salary number when we are changing position filter
    this.state.animateSalary = positionFilter === nextProps.positionFilter;
  }

  componentDidMount() {
    const { showWidget } = this.props;

    if (showWidget) {
      this.scrollTop();
    }
  }

  scrollTop() {
    if (isElementInViewport(this.ref)) {
      return;
    }
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    const { top } = this.ref.getBoundingClientRect();
    const OFFSET = 220;
    window.scrollTo(0, scrollTop + top - OFFSET);
  }

  handleRef = (ref: HTMLElement) => {
    this.ref = ref;
  };

  handleSelect = () => {
    const { id, idStr, isTeam, onSelect, positions } = this.props;
    onSelect({ id, idStr, isTeam }, positions);
  };

  handleRemove = () => {
    const { idStr, onRemove, selectedPosition } = this.props;
    onRemove(idStr, selectedPosition);
  };

  handleClick = () => {
    const { idStr, onClick } = this.props;
    onClick(idStr);
  };

  render() {
    const {
      awayTeam,
      barrier,
      canAfford,
      disabled,
      gameOrder,
      homeTeam,
      image,
      initials,
      isHome,
      isRacing,
      isScratched,
      labels,
      name,
      number,
      onClick,
      playerSelectionTableInfo,
      positionStr,
      reverseOrder,
      rider,
      riderImage,
      selected,
      showWidget,
      tableData,
      trainer
    } = this.props;

    const { animateSalary } = this.state;

    const avatarImage = isRacing ? riderImage : image;

    return (
      <div ref={this.handleRef}>
        <EntityWrapper disabled={disabled} highlight={selected || showWidget}>
          {(avatarImage || initials) && (
            <AvatarCell>
              <PlayerAvatar fallbackText={initials} image={avatarImage} />
            </AvatarCell>
          )}
          <EntityMain>
            <Tappable component={PlayerBarOuter} onTap={onClick ? this.handleClick : undefined}>
              <PlayerBar
                awayTeam={awayTeam}
                barrier={barrier}
                homeTeam={homeTeam}
                isHome={isHome}
                isRacing={isRacing}
                isScratched={isScratched}
                labels={labels}
                name={name}
                number={number}
                gameOrder={gameOrder}
                position={positionStr}
                reverseOrder={reverseOrder}
                rider={rider}
                trainer={trainer}
              />
            </Tappable>
            {isRacing ? (
              <OpenEntityStats alignItems="flex-start">
                <Responsive desktop>
                  <PlayerDatum
                    desktopSize={get('defaultColumn.size', playerSelectionTableInfo) || 'large'}
                  >
                    <StyledAnimateNumber
                      disabled={disabled}
                      warn={!selected && !disabled && !canAfford}
                      format={
                        get(playerSelectionTableInfo.defaultColumn.format, tableData) || '0.00'
                      }
                      number={
                        get(playerSelectionTableInfo.defaultColumn.field, tableData) || '\u2012'
                      }
                    />
                    {get(playerSelectionTableInfo.defaultColumn.field, tableData) > 0 && (
                      <StarSymbol />
                    )}
                  </PlayerDatum>
                  <OpenEntityStatsInfo justifyContent="flex-end">
                    {playerSelectionTableInfo &&
                      Array.isArray(playerSelectionTableInfo.columns) &&
                      playerSelectionTableInfo.columns
                        .filter(
                          ({ field, showInPlayerBar = false }) =>
                            field !== playerSelectionTableInfo.defaultColumn.field &&
                            !showInPlayerBar
                        )
                        .map(
                          ({
                            bold = false,
                            field,
                            format = '0.0',
                            disableWhenUnselected = false,
                            label,
                            showStarSymbolForScheduled = false,
                            size = 'small'
                          }) => {
                            let data = '\u2012';
                            if (get(field, tableData)) {
                              switch (format) {
                                case 'lineGraph':
                                  data = <LineGraph value={get(field, tableData)} />;
                                  break;
                                case 'string':
                                  data = get(field, tableData);
                                  break;
                                default:
                                  data = numeral(get(field, tableData)).format(format);
                              }
                            }
                            return (
                              <PlayerDatum
                                key={field}
                                bold={bold}
                                label={label}
                                disabled={disableWhenUnselected && disabled}
                                mobileSize={size}
                                desktopSize={size}
                              >
                                {data}
                                {showStarSymbolForScheduled && get(field, tableData) > 0 && (
                                  <StarSymbol />
                                )}
                              </PlayerDatum>
                            );
                          }
                        )}
                  </OpenEntityStatsInfo>
                </Responsive>
                <Responsive tabletPortrait tabletLandscape>
                  <OpenEntityStatsInfo justifyContent="flex-start">
                    <PlayerDatum flexDirection="row" label="W">
                      <StyledAnimateNumber
                        disabled={disabled}
                        warn={!selected && !disabled && !canAfford}
                        format="0.00"
                        number={get('winProjected', tableData) || '\u2012'}
                      />
                      {get('winProjected', tableData) > 0 && <StarSymbol />}
                    </PlayerDatum>
                    <PlayerDatum flexDirection="row" label="P">
                      <StyledAnimateNumber
                        disabled={disabled}
                        warn={!selected && !disabled && !canAfford}
                        format="0.00"
                        number={get('placeProjected', tableData) || '\u2012'}
                      />
                      {get('placeProjected', tableData) > 0 && <StarSymbol />}
                    </PlayerDatum>
                  </OpenEntityStatsInfo>
                  <OpenEntityStatsInfo justifyContent="flex-start">
                    <PlayerDatum key="form" label="F" mobileSize="small" flexDirection="row">
                      {get('form', tableData) || '\u2012'}
                    </PlayerDatum>
                    <PlayerDatum key="weight" label="WT" mobileSize="small" flexDirection="row">
                      {get('weight', tableData)
                        ? numeral(get('weight', tableData)).format('0.00')
                        : '\u2012'}
                    </PlayerDatum>
                  </OpenEntityStatsInfo>
                </Responsive>
                <Responsive mobilePortrait mobileLandscape>
                  <Columns className="is-mobile">
                    <Column className="column is-half">
                      <PlayerDatum flexDirection="row" label="W" justifyContent="flex-start">
                        <StyledAnimateNumber
                          disabled={disabled}
                          warn={!selected && !disabled && !canAfford}
                          format="0.00"
                          number={get('winProjected', tableData) || '\u2012'}
                        />
                        {get('winProjected', tableData) > 0 && <StarSymbol />}
                      </PlayerDatum>
                      <PlayerDatum
                        key="form"
                        label="F"
                        mobileSize="small"
                        flexDirection="row"
                        justifyContent="flex-start"
                      >
                        {get('form', tableData) || '\u2012'}
                      </PlayerDatum>
                    </Column>
                    <Column className="column is-half">
                      <PlayerDatum flexDirection="row" label="P" justifyContent="flex-start">
                        <StyledAnimateNumber
                          disabled={disabled}
                          warn={!selected && !disabled && !canAfford}
                          format="0.00"
                          number={get('placeProjected', tableData) || '\u2012'}
                        />
                        {get('placeProjected', tableData) > 0 && <StarSymbol />}
                      </PlayerDatum>
                      <PlayerDatum
                        key="weight"
                        label="WT"
                        mobileSize="small"
                        flexDirection="row"
                        justifyContent="flex-start"
                      >
                        {get('weight', tableData)
                          ? numeral(get('weight', tableData)).format('0.00')
                          : '\u2012'}
                      </PlayerDatum>
                    </Column>
                  </Columns>
                </Responsive>
              </OpenEntityStats>
            ) : (
              <OpenEntityStats>
                <PlayerDatum desktopSize="large">
                  <StyledAnimateNumber
                    disabled={disabled}
                    warn={!selected && !disabled && !canAfford}
                    format="$0,0"
                    number={tableData.salary}
                    animate={animateSalary}
                  />
                </PlayerDatum>
                <OpenEntityStatsInfo>
                  <PlayerDatum
                    label={tableData.popularity !== undefined ? 'Sel' : ''}
                    mobileSize="small"
                    desktopSize="small"
                  >
                    {tableData.popularity !== undefined && (
                      <LineGraph value={tableData.popularity} />
                    )}
                  </PlayerDatum>
                  <PlayerDatum label="Avg" mobileSize="small" desktopSize="small">
                    {tableData.average ? numeral(tableData.average).format('0.0') : '\u2012'}
                  </PlayerDatum>
                  <PlayerDatum label="Form" mobileSize="small" desktopSize="small">
                    {tableData.form ? numeral(tableData.form).format('0.0') : '\u2012'}
                  </PlayerDatum>
                </OpenEntityStatsInfo>
              </OpenEntityStats>
            )}
          </EntityMain>
          <ButtonCell>
            <RowButton
              disabled={disabled}
              type={selected ? 'selected' : 'add'}
              onSelect={selected ? this.handleRemove : this.handleSelect}
            />
          </ButtonCell>
        </EntityWrapper>
      </div>
    );
  }
}

export default OpenEntityRow;
